import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CaretRight, Check, ShieldCheck } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { MAX_INTRO_SECONDS, type ProfileResponse } from '@app/shared';
import { ThoughtRecorder } from '../components/ThoughtRecorder';
import { VoiceSettingsCard } from '../components/VoiceSettingsCard';
import { AutoTextarea, Button, Card, Spinner } from '../components/ui';
import { useIntroOutcome, useLiveProfile } from '../hooks/useIntro';
import { useToast } from '../hooks/useToast';
import { useT } from '../i18n';
import { api, ApiError } from '../lib/api';
import { authorChanges } from '../lib/author-form';
import { cn } from '../lib/cn';
import { errorText } from '../lib/errors';

const MAX_ABOUT_CHARS = 1000;

const inputClass =
  'w-full h-11 px-3 rounded-lg text-base bg-surface-2 border-0 placeholder:text-hint focus:outline-none focus:ring-2 focus:ring-ink/70';

function SectionTitle({ children }: { children: string }) {
  return <h2 className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2">{children}</h2>;
}

/** «Профиль» (spec 6.1): the author's signature, the about text, the voice sample, the language; the admin link. */
export function SettingsPage() {
  const t = useT();
  const qc = useQueryClient();
  const { toast, show } = useToast();
  const profile = useLiveProfile();
  const author = profile?.user.author ?? { name: null, handle: null, about: null };
  const introStatus = profile?.user.introStatus;

  const [name, setName] = useState(author.name ?? '');
  const [handle, setHandle] = useState(author.handle ?? '');
  const [about, setAbout] = useState(author.about ?? '');
  const [handleInvalid, setHandleInvalid] = useState(false);
  const [saving, setSaving] = useState(false);

  // A new spoken introduction rewrites the about text: take it unless the field is being edited.
  const aboutTouched = useRef(false);
  useEffect(() => {
    if (!aboutTouched.current) setAbout(author.about ?? '');
  }, [author.about]);

  useIntroOutcome(introStatus, (outcome) =>
    outcome === 'done' ? show(t.settings.aboutUpdated, 'success') : show(t.intro.failed, 'warning'),
  );

  const changes = authorChanges(author, { name, handle, about });
  const dirty = Object.keys(changes).length > 0;
  const saveProfile = (p: ProfileResponse) => qc.setQueryData<ProfileResponse>(['profile'], p);

  const save = async () => {
    setSaving(true);
    setHandleInvalid(false);
    try {
      const p = await api.updateAuthor(changes);
      saveProfile(p);
      setName(p.user.author.name ?? '');
      setHandle(p.user.author.handle ?? '');
      setAbout(p.user.author.about ?? '');
      aboutTouched.current = false;
      show(t.settings.saved, 'success');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_HANDLE') setHandleInvalid(true);
      else show(errorText(err, t), 'destructive');
    } finally {
      setSaving(false);
    }
  };

  const reRecord = async (blob: Blob, _mime: string, durationMs: number) => {
    try {
      saveProfile(await api.sendIntro(blob, durationMs));
      return true;
    } catch (err) {
      show(errorText(err, t), 'destructive');
      return false;
    }
  };

  return (
    <div className="pt-2">
      <h1 className="text-xl font-extrabold mb-4">{t.settings.title}</h1>

      <section className="mb-5">
        <SectionTitle>{t.settings.signature}</SectionTitle>
        <Card variant="surface" padding="md">
          <p className="text-xs text-hint mb-3">{t.settings.signatureHint}</p>
          <label className="block text-sm font-medium mb-1" htmlFor="author-name">
            {t.settings.name}
          </label>
          <input
            id="author-name"
            className={cn(inputClass, 'mb-3')}
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
          />
          <label className="block text-sm font-medium mb-1" htmlFor="author-handle">
            {t.settings.handle}
          </label>
          <input
            id="author-handle"
            className={cn(inputClass, handleInvalid && 'ring-2 ring-destructive/60 focus:ring-destructive/60')}
            value={handle}
            maxLength={60}
            placeholder={t.settings.handleHint}
            autoCapitalize="off"
            autoCorrect="off"
            onChange={(e) => {
              setHandle(e.target.value);
              setHandleInvalid(false);
            }}
          />
          <p className={cn('text-xs mt-1', handleInvalid ? 'text-destructive' : 'text-hint')}>
            {handleInvalid ? t.settings.handleInvalid : t.settings.handleHint}
          </p>
        </Card>
      </section>

      <section className="mb-5">
        <SectionTitle>{t.settings.about}</SectionTitle>
        <Card variant="surface" padding="md">
          <p className="text-xs text-hint mb-2">{t.settings.aboutHint}</p>
          <AutoTextarea
            value={about}
            placeholder={t.settings.aboutEmpty}
            maxLength={MAX_ABOUT_CHARS}
            minRows={3}
            maxRows={12}
            disabled={introStatus === 'processing'}
            className="text-sm"
            onChange={(e) => {
              aboutTouched.current = true;
              setAbout(e.target.value);
            }}
          />
          <div className="mt-3">
            {introStatus === 'processing' ? (
              <p className="flex items-center gap-2 text-sm text-hint">
                <Spinner size="sm" />
                {t.intro.processing}
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <ThoughtRecorder
                  size="sm"
                  maxSeconds={MAX_INTRO_SECONDS}
                  botUrl={profile?.botUrl ?? null}
                  onRecorded={reRecord}
                  onChatFallback={() => api.setVoiceIntent('intro')}
                  onFailed={(err) => show(errorText(err, t), 'destructive')}
                />
                <span className="text-sm text-hint">{t.settings.reRecord}</span>
              </div>
            )}
          </div>
        </Card>
      </section>

      {/* One save for both cards above: name, handle and the about text. */}
      <Button variant="primary" size="lg" fullWidth disabled={!dirty} loading={saving} onClick={save} className="mb-6">
        {t.settings.save}
      </Button>

      <VoiceSettingsCard />

      <section className="mb-5">
        <SectionTitle>{t.settings.language}</SectionTitle>
        <Card variant="surface" padding="md" className="flex items-center justify-between">
          <span className="text-sm">{t.settings.russian}</span>
          <Check size={18} weight="bold" />
        </Card>
      </section>

      {profile?.user.isAdmin && (
        <Link
          to="/admin"
          className="flex items-center gap-3 p-4 mb-5 rounded-xl bg-surface shadow-card transition-opacity active:opacity-80"
        >
          <ShieldCheck size={20} />
          <span className="flex-1 text-sm font-semibold">{t.settings.admin}</span>
          <CaretRight size={16} className="text-hint" />
        </Link>
      )}

      {toast}
    </div>
  );
}
